// src/Pages/Studio/UserSettings.js

import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Link, useParams, useNavigate } from 'react-router-dom';
import { signOut, sendPasswordResetEmail } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, increment, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, functions, storage } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import { useContactModal } from '../../../context/modalDisclosureContexts';
import { useCookieConsent } from '../../../context/CookieConsentContext';
import toast from 'react-hot-toast';
import firebaseErrorMessage from '../../../utils/firebaseErrorMessage';
import { useOnboarding } from '../../../hooks/useOnboarding';
import { studioMyStudioPath } from '../../../constants/studioProjects';
import {
  workspaceBrandLogoStoragePath,
  workspaceBrandingRef,
  getWorkspaceBrandingSnap,
} from '../../../constants/workspaceBranding';
import './UserSettings.css';

const CONFIRM_PHRASE = 'DELETE';

const UserSettings = () => {
  const { studioID } = useParams();
  const { user, setAuthSessionOverlay, firebaseAuthUser } = useAuth();
  const { openModal: openContactModal } = useContactModal();
  const { openCookieSettings } = useCookieConsent();
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmValue, setDeleteConfirmValue] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notificationsOn, setNotificationsOn] = useState(true);
  const [signOutLoading, setSignOutLoading] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [onboardingToggleLoading, setOnboardingToggleLoading] = useState(false);
  const [restartOnboardingLoading, setRestartOnboardingLoading] = useState(false);
  const [brandLogoUrl, setBrandLogoUrl] = useState('');
  const [brandLogoUploading, setBrandLogoUploading] = useState(false);
  const brandingRef = user?.uid ? workspaceBrandingRef(db, user.uid) : null;
  const {
    onboardingEnabled,
    setOnboardingEnabled,
    restartOnboarding,
  } = useOnboarding();
  const settingsRef = user?.uid ? doc(db, 'users', user.uid, 'settings', 'preferences') : null;

  useEffect(() => {
    let cancelled = false;
    if (!settingsRef || !user?.uid) return () => { cancelled = true; };
    (async () => {
      try {
        const snap = await getDoc(settingsRef);
        if (!snap.exists()) {
          await setDoc(settingsRef, {
            userId: user.uid,
            notificationsOn: true,
            signOutEverywhereClickCount: 0,
            deleteAccountAttemptCount: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }, { merge: true });
          if (!cancelled) setNotificationsOn(true);
          return;
        }
        if (!cancelled) setNotificationsOn(snap.data()?.notificationsOn !== false);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [settingsRef, user?.uid]);

  useEffect(() => {
    let cancelled = false;
    if (!brandingRef) return () => { cancelled = true; };
    (async () => {
      try {
        const snap = await getWorkspaceBrandingSnap(db, user.uid);
        if (cancelled) return;
        const url = snap.exists() ? snap.data()?.logoUrl : '';
        setBrandLogoUrl(typeof url === 'string' ? url : '');
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [brandingRef, user?.uid]);

  const handleToggleNotifications = async () => {
    const next = !notificationsOn;
    setNotificationsOn(next);
    if (!settingsRef) return;
    try {
      await setDoc(settingsRef, {
        userId: user?.uid || null,
        notificationsOn: next,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      if (user?.uid) {
        await setDoc(doc(db, 'users', user.uid), { notificationsOn: next }, { merge: true });
      }
    } catch (err) {
      setNotificationsOn(!next);
      toast.error(firebaseErrorMessage(err, 'Failed to update notifications setting'));
    }
  };

  const handleToggleOnboarding = async () => {
    setOnboardingToggleLoading(true);
    try {
      await setOnboardingEnabled(!onboardingEnabled);
    } catch (err) {
      toast.error(firebaseErrorMessage(err, 'Failed to update onboarding setting'));
    } finally {
      setOnboardingToggleLoading(false);
    }
  };

  const handleRestartOnboarding = async () => {
    const targetUid = studioID || user?.uid;
    if (!targetUid) return;
    setRestartOnboardingLoading(true);
    try {
      await restartOnboarding();
      toast.success('Onboarding restarted.');
      navigate(studioMyStudioPath(targetUid), { replace: true });
    } catch (err) {
      toast.error(firebaseErrorMessage(err, 'Failed to restart onboarding'));
    } finally {
      setRestartOnboardingLoading(false);
    }
  };

  const handleBrandLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user?.uid || !brandingRef) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Upload a PNG, JPEG, or WebP logo');
      return;
    }
    setBrandLogoUploading(true);
    try {
      const path = workspaceBrandLogoStoragePath(user.uid);
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file, { contentType: file.type });
      const url = await getDownloadURL(sref);
      await setDoc(brandingRef, {
        logoUrl: url,
        logoStoragePath: path,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setBrandLogoUrl(url);
      toast.success('Brand logo saved — will apply to video exports when available');
    } catch (err) {
      toast.error(firebaseErrorMessage(err, 'Could not upload logo'));
    } finally {
      setBrandLogoUploading(false);
    }
  };

  const handleDeleteAccountClick = () => {
    setDeleteConfirmValue('');
    setDeleteModalOpen(true);
  };

  const handleDeleteAccountConfirm = async () => {
    if (deleteConfirmValue.trim() !== CONFIRM_PHRASE || !user?.uid) return;
    setDeleteLoading(true);
    setAuthSessionOverlay(true);
    try {
      // Account deletion is handled entirely by the deleteAccount callable (Admin SDK).
      // Avoid a client Firestore write here: increment() merges can surface permission-denied
      // in some setups, and the user subtree is deleted immediately after anyway.
      const deleteAccount = httpsCallable(functions, 'deleteAccount');
      await deleteAccount();
      await signOut(auth);
      toast.success('Account deleted. You have been signed out.');
      navigate('/login', { replace: true });
    } catch (err) {
      setAuthSessionOverlay(false);
      toast.error(firebaseErrorMessage(err, 'Failed to delete account'));
      if (process.env.NODE_ENV === 'development') {
        console.error('deleteAccount error:', err);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteModalClose = useCallback(() => {
    if (!deleteLoading) {
      setDeleteModalOpen(false);
      setDeleteConfirmValue('');
    }
  }, [deleteLoading]);

  const isPasswordAccount = Boolean(
    firebaseAuthUser?.providerData?.some((p) => p?.providerId === 'password'),
  );
  const accountEmail = (firebaseAuthUser?.email || user?.email || '').trim();

  const handleSendPasswordReset = async () => {
    if (!isPasswordAccount || !accountEmail || passwordResetLoading) return;
    setPasswordResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, accountEmail, {
        url: window.location.origin + '/login',
      });
      toast.success('If an account exists, we sent a reset link to your email.');
    } catch (err) {
      toast.error(firebaseErrorMessage(err, 'Could not send reset email. Please try again.'));
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleSignOutEverywhere = async () => {
    setSignOutLoading(true);
    setAuthSessionOverlay(true);
    try {
      if (settingsRef && user?.uid) {
        await setDoc(settingsRef, {
          userId: user.uid,
          signOutEverywhereClickCount: increment(1),
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }
      await signOut(auth);
      toast.success('Signed out from all sessions.');
      navigate('/login', { replace: true });
    } catch (err) {
      setAuthSessionOverlay(false);
      toast.error(firebaseErrorMessage(err, 'Failed to sign out'));
    } finally {
      setSignOutLoading(false);
    }
  };

  useEffect(() => {
    if (!deleteModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleDeleteModalClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleteModalOpen, handleDeleteModalClose]);

  return (
    <div className="studio-settings">
      {/* Mobile sidebar - Settings, Account, Support sections */}
      <aside className="studio-settings-mobile-sidebar">
        <div className="studio-section">
          <div className="studio-section-title">Settings</div>
          <NavLink to={`/studio/${studioID}/settings`} className={({ isActive }) => `studio-nav-item${isActive ? ' is-active' : ''}`}>Settings</NavLink>
        </div>
        <div className="studio-section">
          <div className="studio-section-title">Account</div>
          <NavLink to={`/studio/${studioID}/billing`} className={({ isActive }) => `studio-nav-item${isActive ? ' is-active' : ''}`}>Billing</NavLink>
          <Link to="/pricing" className="studio-nav-item">Pricing</Link>
        </div>
        <div className="studio-section">
          <div className="studio-section-title">Support</div>
          <Link to="/documentation" className="studio-nav-item">Help</Link>
          <button type="button" className="studio-nav-item" onClick={openContactModal}>Contact Us</button>
        </div>
      </aside>

      <div className="studio-settings-content">
        <header className="studio-page-heading">
          <h1 className="studio-page-title">Settings</h1>
        </header>
        <div className="studio-page-content">
          <section className="studio-settings-box studio-settings-privacy">
            <h2 className="studio-settings-box-title">Privacy &amp; sharing</h2>
            <p className="studio-settings-desc">
              Your captures stay private in your workspace. Share one safely with collaborators using a <strong>Share link</strong> from the capture list or viewer. Demo scenes on the marketing site are curated by the Volugraph team (demo uploads are admin-only).
            </p>
            <div className="studio-settings-privacy-cookie-block">
              <div className="studio-settings-privacy-cookie-copy">
                <h3 className="studio-settings-box-title studio-settings-privacy-cookie-title">Cookies &amp; essential storage</h3>
                <p className="studio-settings-desc">
                  Open the same cookie notice you see on first visit to review how we use essential cookies
                  and browser storage (no analytics or marketing trackers).
                </p>
              </div>
              <button
                type="button"
                className="studio-settings-privacy-cookie-btn"
                onClick={openCookieSettings}
              >
                Cookie notice
              </button>
            </div>
          </section>

          {/* Notifications / preferences card */}
          <div className="studio-settings-section">
            <div className="studio-settings-card">
              <span className="studio-settings-card-icon-wrap" aria-hidden="true">
                <i className="ph ph-envelope-simple" aria-hidden="true" />
              </span>
              <div className="studio-settings-card-body">
                <span className="studio-settings-card-title">Product &amp; technology email</span>
                <span className="studio-settings-card-desc">
                  Optional: emails about major feature launches and technology updates. This is not
                  required for billing, security, or account messages.
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={notificationsOn}
                aria-label="Opt in to product and technology email updates"
                className={`studio-settings-toggle${notificationsOn ? ' is-on' : ''}`}
                onClick={handleToggleNotifications}
              >
                <span className="studio-settings-toggle-track" />
              </button>
            </div>
          </div>

          <div className="studio-settings-section">
            <div className="studio-settings-card studio-settings-card-action studio-settings-card-brand">
              <span className="studio-settings-card-icon-wrap" aria-hidden="true">
                <i className="ph ph-image" aria-hidden="true" />
              </span>
              <div className="studio-settings-card-body">
                <span className="studio-settings-card-title">Brand logo (video exports)</span>
                <span className="studio-settings-card-desc">
                  Upload your logo for cinematic flythrough exports. Applied automatically once video export ships.
                </span>
                {brandLogoUrl ? (
                  <img src={brandLogoUrl} alt="" className="studio-settings-brand-logo-preview" />
                ) : null}
              </div>
              <label className="studio-settings-card-btn studio-settings-brand-upload-label">
                {brandLogoUploading ? 'Uploading…' : brandLogoUrl ? 'Replace logo' : 'Upload logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="studio-settings-brand-upload-input"
                  onChange={handleBrandLogoUpload}
                  disabled={brandLogoUploading}
                />
              </label>
            </div>
          </div>

          <div className="studio-settings-section">
            <div className="studio-settings-card studio-settings-card-onboarding">
              <div className="studio-settings-card-onboarding-main">
                <span className="studio-settings-card-icon-wrap" aria-hidden="true">
                  <i className="ph ph-compass" aria-hidden="true" />
                </span>
                <div className="studio-settings-card-body">
                  <span className="studio-settings-card-title">Onboarding tips</span>
                  <span className="studio-settings-card-desc">
                    Show welcome guidance and contextual tips when you explore Studio, uploads, and the 3D workspace.
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={onboardingEnabled}
                  aria-label="Show onboarding tips"
                  className={`studio-settings-toggle${onboardingEnabled ? ' is-on' : ''}`}
                  onClick={handleToggleOnboarding}
                  disabled={onboardingToggleLoading}
                >
                  <span className="studio-settings-toggle-track" />
                </button>
              </div>
              <div className="studio-settings-card-onboarding-restart">
                <p className="studio-settings-card-desc">
                  Replay the welcome modal and reset contextual tips from My Studio.
                </p>
                <button
                  type="button"
                  className="studio-settings-card-btn"
                  onClick={handleRestartOnboarding}
                  disabled={restartOnboardingLoading}
                >
                  {restartOnboardingLoading ? 'Restarting…' : 'Restart'}
                </button>
              </div>
            </div>
          </div>

          <div className="studio-settings-section">
            <div className="studio-settings-card studio-settings-card-action studio-settings-card-password">
              <span className="studio-settings-card-icon-wrap" aria-hidden="true">
                <i className="ph ph-lock-key" aria-hidden="true" />
              </span>
              <div className="studio-settings-card-body">
                <span className="studio-settings-card-title">Password</span>
                <span className="studio-settings-card-desc">
                  {isPasswordAccount
                    ? accountEmail
                      ? `We'll email a link to ${accountEmail} so you can choose a new password.`
                      : "We'll email a link to your account email so you can choose a new password."
                    : 'Password is managed by your sign-in provider.'}
                </span>
              </div>
              <button
                type="button"
                className="studio-settings-card-btn"
                onClick={handleSendPasswordReset}
                disabled={!isPasswordAccount || !accountEmail || passwordResetLoading}
                title={
                  !isPasswordAccount
                    ? 'Password is managed by your sign-in provider'
                    : !accountEmail
                      ? 'No email address on this account'
                      : undefined
                }
              >
                {passwordResetLoading ? 'Sending…' : 'Send reset email'}
              </button>
            </div>
          </div>

          {/* Active sessions */}
          <div className="studio-settings-section">
            <div className="studio-settings-card studio-settings-card-action">
              <span className="studio-settings-card-icon-wrap" aria-hidden="true">
                <i className="ph ph-sign-out" aria-hidden="true" />
              </span>
              <div className="studio-settings-card-body">
                <span className="studio-settings-card-title">Sign out everywhere</span>
                <span className="studio-settings-card-desc">Sign out from all devices and sessions.</span>
              </div>
              <button
                type="button"
                className="studio-settings-card-btn"
                onClick={handleSignOutEverywhere}
                disabled={signOutLoading}
              >
                {signOutLoading ? 'Signing out…' : 'Sign Out'}
              </button>
            </div>
          </div>

          <div className="studio-settings-section">
            <div className="studio-settings-card studio-settings-card-action studio-settings-card-danger">
              <div className="studio-settings-card-body">
                <span className="studio-settings-card-title studio-settings-danger-title">Danger zone</span>
                <span className="studio-settings-card-desc">
                  Permanently delete your Volugraph account, workspace content, and linked profile
                  data. Some billing and tax records may be retained as required by law; see our{' '}
                  <Link to="/privacy">Privacy Policy</Link>. This cannot be undone.
                </span>
              </div>
              <button
                type="button"
                className="studio-settings-card-btn studio-settings-card-btn-danger"
                onClick={handleDeleteAccountClick}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting…' : 'DELETE ACCOUNT'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {deleteModalOpen && (
        <div
          className="studio-settings-delete-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="studio-settings-delete-modal-title"
          onClick={(e) => e.target === e.currentTarget && handleDeleteModalClose()}
        >
          <div className="studio-settings-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h2 id="studio-settings-delete-modal-title" className="studio-settings-delete-modal-title">Delete account?</h2>
            <p className="studio-settings-delete-modal-text">
              Your account and workspace data will be permanently deleted. Limited billing or legal
              records may be kept where the law requires — see the Privacy Policy.
            </p>
            <p className="studio-settings-delete-modal-hint">
              Type <strong>{CONFIRM_PHRASE}</strong> to confirm.
            </p>
            <input
              type="text"
              className="studio-settings-delete-modal-input"
              value={deleteConfirmValue}
              onChange={(e) => setDeleteConfirmValue(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              aria-label="Confirmation"
              disabled={deleteLoading}
              autoComplete="off"
            />
            <div className="studio-settings-delete-modal-actions">
              <button
                type="button"
                className="studio-settings-delete-modal-btn studio-settings-delete-modal-btn-cancel"
                onClick={handleDeleteModalClose}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="studio-settings-delete-modal-btn studio-settings-delete-modal-btn-confirm"
                onClick={handleDeleteAccountConfirm}
                disabled={deleteConfirmValue.trim() !== CONFIRM_PHRASE || deleteLoading}
              >
                {deleteLoading ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSettings;
